/**
 * 堡垒锻造渲染器的视觉特效系统。
 *
 * 纯视觉效果——不触碰 GameState 也不发出任何事件。
 * 使用 Phaser Graphics 实现粒子和补间动画。
 *
 * 支持的特效类型：
 *   - DeathExplosion  : 敌人死亡时的径向粒子爆发
 *   - SkillEffect     : 范围闪光 + 扩散光环（火/霜/毒等）
 *   - DamageNumber    : 命中目标上方浮动的伤害数值
 *   - UpgradeFlash    : 塔建造/升级时的短暂明亮脉冲
 */

import Phaser from "phaser";
import type { DamageType } from "@bastion/data";

// ─── 配置接口 ──────────────────────────────────────────────────────────

export interface DeathEffectConfig {
  count: number;
  minSpeed: number;
  maxSpeed: number;
  minSize: number;
  maxSize: number;
  lifetime: number;
  colors: number[];
}

export interface SkillEffectConfig {
  radius: number;
  ringWidth: number;
  flashAlpha: number;
  lifetime: number;
  particleCount: number;
  particleSpeed: number;
  colors: number[];
}

export interface DamageNumberConfig {
  fontSize: string;
  fontFamily: string;
  lifetime: number;
  floatSpeed: number;
  colors: Record<DamageType, number>;
}

export interface UpgradeFlashConfig {
  radius: number;
  initialAlpha: number;
  lifetime: number;
}

export interface EffectsSystemOptions {
  death?: Partial<DeathEffectConfig>;
  skills?: Record<string, Partial<SkillEffectConfig>>;
  damageNumbers?: Partial<DamageNumberConfig>;
  upgradeFlash?: Partial<UpgradeFlashConfig>;
}

// ─── 公共 API ─────────────────────────────────────────────────────────

/**
 * Central effects hub that owns all active effect objects.
 * Callers enqueue effects; the system renders them each frame and
 * cleans up expired ones via Phaser tween completion callbacks.
 */
export class EffectsSystem {
  private readonly scene: Phaser.Scene;
  /** 活跃的粒子图形对象（类型声明为 unknown 以避免 Phaser 类型不匹配）。 */
  private readonly particles: Array<{ obj: unknown; active: boolean }> = [];
  /** 活跃的闪光/光环图形对象。 */
  private readonly flashes: Array<{ obj: unknown; active: boolean }> = [];
  /** 活跃的浮动文本对象。 */
  private readonly texts: Array<{ obj: Phaser.GameObjects.Text; active: boolean }> = [];

  private readonly deathConfig: DeathEffectConfig;
  private readonly skillConfig: Record<string, SkillEffectConfig>;
  private readonly dmgConfig: DamageNumberConfig;
  private readonly upgradeConfig: UpgradeFlashConfig;
  private readonly meteorFlights = new Map<
    string,
    {
      elapsed: number;
      target: { x: number; y: number };
      glow: Phaser.GameObjects.Arc;
      shell: Phaser.GameObjects.Ellipse;
      core: Phaser.GameObjects.Ellipse;
      trail: Phaser.GameObjects.Graphics;
      trailPoints: Array<{ x: number; y: number }>;
      lastEmberAt: number;
      direction: number;
    }
  >();

  /** 设为 true 时抑制所有视觉更新（对无头/E2E 运行很有用）。 */
  disabled = false;
  private readonly glacialStormVisuals = new Map<
    string,
    {
      layers: Phaser.GameObjects.Container[];
      maskGraphics: Phaser.GameObjects.Graphics;
      geometryMask: Phaser.Display.Masks.GeometryMask;
      durationSeconds: number;
    }
  >();

  constructor(scene: Phaser.Scene, options: EffectsSystemOptions = {}) {
    this.scene = scene;
    this.deathConfig = {
      count: 12,
      minSpeed: 60,
      maxSpeed: 160,
      minSize: 3,
      maxSize: 7,
      lifetime: 0.6,
      colors: [0xff6b35, 0xf7c948, 0xef4444, 0xf59e0b],
      ...options.death,
    };
    this.skillConfig = {
      fire: {
        radius: 150,
        ringWidth: 8,
        flashAlpha: 0.25,
        lifetime: 0.9,
        particleCount: 24,
        particleSpeed: 100,
        colors: [0xff4500, 0xff6b35, 0xf7c948, 0xff0000],
      },
      frost: {
        radius: 120,
        ringWidth: 6,
        flashAlpha: 0.2,
        lifetime: 1.1,
        particleCount: 20,
        particleSpeed: 80,
        colors: [0x7dd3fc, 0x38bdf8, 0x0ea5e9, 0xcffafe],
      },
      poison: {
        radius: 100,
        ringWidth: 5,
        flashAlpha: 0.18,
        lifetime: 1.0,
        particleCount: 16,
        particleSpeed: 60,
        colors: [0x22c55e, 0x86efac, 0x166534, 0x4ade80],
      },
      magic: {
        radius: 130,
        ringWidth: 6,
        flashAlpha: 0.22,
        lifetime: 0.8,
        particleCount: 18,
        particleSpeed: 90,
        colors: [0xa855f7, 0xc084fc, 0x7c3aed, 0xe9d5ff],
      },
      physical: {
        radius: 80,
        ringWidth: 4,
        flashAlpha: 0.15,
        lifetime: 0.5,
        particleCount: 8,
        particleSpeed: 70,
        colors: [0x94a3b8, 0xe2e8f0, 0x64748b, 0xf1f5f9],
      },
      ...options.skills,
    };
    this.dmgConfig = {
      fontSize: "16px",
      fontFamily: "monospace",
      lifetime: 1.2,
      floatSpeed: 50,
      colors: {
        physical: 0xfbbf24,

        magic: 0xa855f7,
      },
      ...options.damageNumbers,
    };
    this.upgradeConfig = {
      radius: 30,
      initialAlpha: 0.6,
      lifetime: 0.7,
      ...options.upgradeFlash,
    };
  }

  // ─── 辅助函数 ─────────────────────────────────────────────────────────

  private makeCircle(x: number, y: number, radius: number, color: number, alpha: number): unknown {
    return this.scene.add.circle(x, y, radius, color, alpha);
  }

  // ─── 入队特效点 ──────────────────────────────────────────────────

  /** 在给定世界位置生成径向粒子爆发。 */
  spawnDeathEffect(x: number, y: number, isBoss: boolean = false): void {
    if (this.disabled) return;
    const cfg = this.deathConfig;
    const count = isBoss ? cfg.count * 2 : cfg.count;
    const baseColor = cfg.colors[Math.floor(Math.random() * cfg.colors.length)];
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
      const speed = cfg.minSpeed + Math.random() * (cfg.maxSpeed - cfg.minSpeed);
      const size = cfg.minSize + Math.random() * (cfg.maxSize - cfg.minSize);
      const obj = this.makeCircle(x, y, size, baseColor, 1);
      this.particles.push({ obj, active: true });
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;
      this.scene.tweens.add({
        targets: obj,
        x: x + vx * cfg.lifetime,
        y: y + vy * cfg.lifetime,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: cfg.lifetime * 1000,
        ease: "Quart.easeIn",
        onComplete: () => {
          this.destroyObj(obj);
          const idx = this.particles.findIndex((p) => p.obj === obj);
          if (idx >= 0) this.particles.splice(idx, 1);
        },
      });
    }
    if (isBoss) {
      this.spawnFlash(x, y, 0xfbbf24, 0.4, 0.6);
    }
  }

  /** 生成范围技能视觉效果：扩散光环 + 向外粒子。 */
  spawnSkillEffect(x: number, y: number, damageType: DamageType): void {
    if (this.disabled) return;
    const cfg = this.skillConfig[damageType] ?? this.skillConfig.physical;

    // 在一切下方的闪光圆圈
    const flash = this.makeCircle(x, y, cfg.radius, cfg.colors[0], cfg.flashAlpha);
    this.flashes.push({ obj: flash, active: true });
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: cfg.lifetime * 500,
      ease: "Power2.out",
      onComplete: () => {
        this.destroyObj(flash);
        const idx = this.flashes.findIndex((p) => p.obj === flash);
        if (idx >= 0) this.flashes.splice(idx, 1);
      },
    });

    // 扩散的光环轮廓
    const ring = this.makeCircle(x, y, cfg.radius, cfg.colors[0], cfg.flashAlpha * 1.5);
    (ring as Phaser.GameObjects.Arc).setStrokeStyle(
      cfg.ringWidth,
      cfg.colors[0],
      cfg.flashAlpha * 1.5,
    );
    this.flashes.push({ obj: ring, active: true });
    this.scene.tweens.add({
      targets: ring,
      scaleX: 1.4,
      scaleY: 1.4,
      alpha: 0,
      duration: cfg.lifetime * 600,
      ease: "Power2.out",
      onComplete: () => {
        this.destroyObj(ring);
        const idx = this.flashes.findIndex((p) => p.obj === ring);
        if (idx >= 0) this.flashes.splice(idx, 1);
      },
    });

    // 向外移动的粒子
    const n = cfg.particleCount;
    for (let i = 0; i < n; i++) {
      const angle = (Math.PI * 2 * i) / n;
      const size = 2 + Math.random() * 3;
      const color = cfg.colors[Math.floor(Math.random() * cfg.colors.length)];
      const obj = this.makeCircle(x, y, size, color, 1);
      this.particles.push({ obj, active: true });
      const dist = cfg.radius * 1.5;
      this.scene.tweens.add({
        targets: obj,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: cfg.lifetime * 800,
        ease: "Quart.easeIn",
        onComplete: () => {
          this.destroyObj(obj);
          const idx = this.particles.findIndex((p) => p.obj === obj);
          if (idx >= 0) this.particles.splice(idx, 1);
        },
      });
    }
  }

  /** 按 GameState 活跃场快照创建/更新/移除每个场实例的冰川风暴视觉。 */
  syncGlacialStorms(fields: readonly import("@bastion/game-core").ActiveSkillField[]): void {
    const activeIds = new Set(fields.map((field) => field.id));
    for (const [id, visual] of this.glacialStormVisuals) {
      if (activeIds.has(id)) continue;
      this.clearGlacialStorm(id, visual);
    }

    for (const field of fields) {
      const existing = this.glacialStormVisuals.get(field.id);
      const visual = existing ?? this.createGlacialStorm(field);
      if (!visual) continue;
      if (!existing) this.glacialStormVisuals.set(field.id, visual);
      const elapsed = Math.max(0, field.durationSeconds - field.remainingSeconds);
      const progress = field.durationSeconds > 0 ? elapsed / field.durationSeconds : 1;
      const fadeIn = Math.min(1, elapsed / Math.min(0.42, field.durationSeconds * 0.18));
      const fadeOut = Math.min(
        1,
        field.remainingSeconds / Math.min(0.62, field.durationSeconds * 0.2),
      );
      const envelope = Phaser.Math.Clamp(Math.min(fadeIn, fadeOut), 0, 1);
      visual.layers[0].setAlpha(0.72 * envelope);
      visual.layers[1].setAlpha(0.88 * envelope);
      visual.layers[2].setAlpha(0.8 * envelope);
      visual.layers[2].setRotation(elapsed * 0.55);
      visual.layers[3].setAlpha(0.9 * envelope);
      visual.layers[4].setAlpha(0.84 * envelope);
      visual.layers[4].setRotation(-elapsed * 0.18);
      visual.layers[4].setY(0);
      visual.layers[4].list.forEach((object, index) => {
        if (!(object instanceof Phaser.GameObjects.Star)) return;
        const phase = (((progress * 1.7 + index * 0.137) % 1) + 1) % 1;
        object.setY(-field.radius * 0.42 + phase * field.radius * 0.84);
        object.setRotation(phase * Math.PI * 4 + index);
        object.setAlpha((0.22 + 0.62 * Math.sin(phase * Math.PI)) * envelope);
      });
    }
  }

  private createGlacialStorm(field: import("@bastion/game-core").ActiveSkillField): {
    layers: Phaser.GameObjects.Container[];
    maskGraphics: Phaser.GameObjects.Graphics;
    geometryMask: Phaser.Display.Masks.GeometryMask;
    durationSeconds: number;
  } | null {
    if (
      this.disabled ||
      !Number.isFinite(field.position.x) ||
      !Number.isFinite(field.position.y) ||
      !Number.isFinite(field.radius) ||
      field.radius <= 0 ||
      !Number.isFinite(field.durationSeconds) ||
      field.durationSeconds <= 0
    ) {
      return null;
    }

    const scene = this.scene;
    const layers: Phaser.GameObjects.Container[] = [];
    const maskGraphics = scene.add.graphics().setDepth(-9);
    maskGraphics.fillStyle(0xffffff, 1);
    maskGraphics.fillCircle(field.position.x, field.position.y, field.radius);
    maskGraphics.setVisible(false);
    const geometryMask = maskGraphics.createGeometryMask();
    const addLayer = (depth: number): Phaser.GameObjects.Container => {
      const layer = scene.add
        .container(field.position.x, field.position.y)
        .setDepth(depth)
        .setMask(geometryMask);
      layers.push(layer);
      this.flashes.push({ obj: layer, active: true });
      return layer;
    };

    const ground = addLayer(-8);
    const frost = scene.add.graphics();
    frost.fillStyle(0x7dd3fc, 0.08);
    frost.fillCircle(0, 0, field.radius);
    frost.lineStyle(2, 0xcffafe, 0.22);
    for (let i = 0; i < 32; i++) {
      const angle = (Math.PI * 2 * i) / 32;
      const inner = field.radius * (0.12 + (i % 4) * 0.035);
      const outer = field.radius * (0.72 + (i % 5) * 0.04);
      frost.beginPath();
      frost.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
      frost.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
      frost.strokePath();
    }
    for (let i = 0; i < 18; i++) {
      const angle = (Math.PI * 2 * i) / 18 + 0.12;
      const distance = field.radius * (0.3 + (i % 3) * 0.14);
      const px = Math.cos(angle) * distance;
      const py = Math.sin(angle) * distance;
      frost.lineStyle(1.5, 0xe0f7ff, 0.28);
      frost.beginPath();
      frost.moveTo(px, py);
      frost.lineTo(px + Math.cos(angle + 0.8) * 20, py + Math.sin(angle + 0.8) * 20);
      frost.lineTo(px + Math.cos(angle - 0.8) * 28, py + Math.sin(angle - 0.8) * 28);
      frost.strokePath();
    }
    ground.add(frost);

    const cloudLayer = addLayer(-7);
    const clouds = scene.add.graphics();
    clouds.fillStyle(0x071a31, 0.24);
    clouds.fillCircle(0, 0, field.radius);
    clouds.fillStyle(0x38bdf8, 0.08);
    for (let i = 0; i < 12; i++) {
      const angle = (Math.PI * 2 * i) / 12;
      const distance = field.radius * (0.18 + (i % 4) * 0.16);
      const cloudRadius = field.radius * (0.18 + (i % 3) * 0.045);
      clouds.fillEllipse(
        Math.cos(angle) * distance,
        Math.sin(angle) * distance,
        cloudRadius * 2,
        cloudRadius * 1.25,
      );
    }
    cloudLayer.add(clouds);

    const vortexLayer = addLayer(-6);
    const vortex = scene.add.graphics();
    vortex.fillStyle(0x38bdf8, 0.035);
    vortex.fillCircle(0, 0, field.radius * 0.58);
    const rings = [
      { scale: 0.34, color: 0xcffafe, alpha: 0.44, line: 3 },
      { scale: 0.58, color: 0x7dd3fc, alpha: 0.3, line: 2.5 },
      { scale: 0.82, color: 0x38bdf8, alpha: 0.18, line: 2 },
    ];
    for (const ring of rings) {
      vortex.lineStyle(ring.line, ring.color, ring.alpha);
      vortex.strokeCircle(0, 0, field.radius * ring.scale);
    }
    vortex.lineStyle(2, 0xbae6fd, 0.18);
    for (let i = 0; i < 20; i++) {
      const angle = (Math.PI * 2 * i) / 20;
      const inner = field.radius * (0.04 + (i % 4) * 0.025);
      const outer = field.radius * (0.3 + (i % 5) * 0.035);
      vortex.beginPath();
      vortex.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
      vortex.lineTo(Math.cos(angle + 0.08) * outer, Math.sin(angle + 0.08) * outer);
      vortex.strokePath();
    }
    vortexLayer.add(vortex);

    const wallLayer = addLayer(-5);
    const wall = scene.add.graphics();
    wall.lineStyle(Math.max(3, field.radius * 0.025), 0xcffafe, 0.42);
    for (let i = 0; i < 36; i++) {
      const angle = (Math.PI * 2 * i) / 36;
      const inner = field.radius * 0.89;
      const outer = field.radius * (0.96 + (i % 3) * 0.012);
      wall.beginPath();
      wall.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
      wall.lineTo(Math.cos(angle + 0.08) * outer, Math.sin(angle + 0.08) * outer);
      wall.strokePath();
    }
    wallLayer.add(wall);

    const snowLayer = addLayer(-4);
    const count = 38;
    for (let i = 0; i < count; i++) {
      const angle = ((i * 137.508) % 360) * (Math.PI / 180);
      const distance = Math.sqrt((i + 0.5) / count) * field.radius * 0.88;
      const size = 3 + ((i * 17) % 7);
      const star = scene.add.star(
        Math.cos(angle) * distance,
        Math.sin(angle) * distance,
        4,
        size * 0.35,
        size,
        0xdff8ff,
        0.68,
      );
      star.setBlendMode(Phaser.BlendModes.ADD);
      snowLayer.add(star);
    }

    return { layers, maskGraphics, geometryMask, durationSeconds: field.durationSeconds };
  }

  private clearGlacialStorm(
    id: string,
    visual: {
      layers: Phaser.GameObjects.Container[];
      maskGraphics: Phaser.GameObjects.Graphics;
      geometryMask: Phaser.Display.Masks.GeometryMask;
      durationSeconds: number;
    },
  ): void {
    visual.geometryMask.destroy();
    visual.maskGraphics.destroy();
    for (const layer of visual.layers) {
      this.destroyObj(layer);
      const index = this.flashes.findIndex((entry) => entry.obj === layer);
      if (index >= 0) this.flashes.splice(index, 1);
    }
    this.glacialStormVisuals.delete(id);
  }

  private clearGlacialStorms(): void {
    for (const [id, visual] of this.glacialStormVisuals) {
      this.clearGlacialStorm(id, visual);
    }
  }

  /** 创建轨道打击陨石飞行对象；飞行进度由 Game Core 模拟时间驱动。 */
  spawnMeteorStrike(impactId: string, targetX: number, targetY: number, progress = 0): void {
    if (this.disabled || !impactId || !Number.isFinite(targetX) || !Number.isFinite(targetY)) {
      return;
    }
    this.cancelMeteorStrike(impactId);
    const scene = this.scene;
    const mapWidth = scene.scale.width || 800;
    const deltaX = Math.max(280, mapWidth - targetX + 36);
    const deltaY = Phaser.Math.Clamp(deltaX * 0.58, 200, 360);
    const startX = targetX + deltaX;
    const startY = targetY - deltaY;
    const fallDuration = 0.72;
    const impactAngle = Math.atan2(deltaY, -deltaX);

    // 陨石头部由暗红热核、熔岩外壳和柔和辉光组成，避免刺眼白闪。
    const glow = scene.add.circle(startX, startY, 26, 0xf97316, 0.16);
    glow.setBlendMode(Phaser.BlendModes.ADD).setDepth(14);
    this.particles.push({ obj: glow, active: true });

    const shell = scene.add.ellipse(startX, startY, 28, 16, 0xdc3b0b, 1);
    shell.setRotation(impactAngle).setBlendMode(Phaser.BlendModes.ADD).setDepth(15);
    this.particles.push({ obj: shell, active: true });

    const core = scene.add.ellipse(startX, startY, 15, 8, 0xff6817, 0.95);
    core.setRotation(impactAngle).setDepth(16);
    this.particles.push({ obj: core, active: true });

    // 尾焰图形在 tween 更新时取样，只绘制陨石已经走过的路径。
    const trail = scene.add.graphics().setDepth(13);
    trail.setBlendMode(Phaser.BlendModes.ADD);
    this.flashes.push({ obj: trail, active: true });
    const flight = {
      elapsed: Phaser.Math.Clamp(progress, 0, 1) * fallDuration,
      target: { x: targetX, y: targetY },
      glow,
      shell,
      core,
      trail,
      trailPoints: [],
      lastEmberAt: -Infinity,
      direction: impactAngle,
    };
    this.meteorFlights.set(impactId, flight);
    this.updateMeteorFlight(flight);
  }

  /** 依据 Game Core 剩余模拟时间同步陨石；暂停时进度保持不变。 */
  syncMeteorStrikes(
    strikes: Array<{ id: string; position: { x: number; y: number }; remainingSeconds: number }>,
  ): void {
    const activeIds = new Set<string>();
    for (const strike of strikes) {
      activeIds.add(strike.id);
      const progress = Phaser.Math.Clamp(1 - strike.remainingSeconds / 0.72, 0, 1);
      let flight = this.meteorFlights.get(strike.id);
      if (!flight) {
        this.spawnMeteorStrike(strike.id, strike.position.x, strike.position.y, progress);
        flight = this.meteorFlights.get(strike.id);
      }
      if (flight) {
        flight.elapsed = progress * 0.72;
        this.updateMeteorFlight(flight);
      }
    }
    for (const impactId of this.meteorFlights.keys()) {
      if (!activeIds.has(impactId)) this.cancelMeteorStrike(impactId);
    }
  }

  /** 落地事件负责终止飞行并播放撞击视觉，不参与伤害裁决。 */
  finishMeteorStrike(impactId: string, landed: boolean, radius = 150): void {
    const flight = this.meteorFlights.get(impactId);
    if (!flight) return;
    this.destroyMeteorFlight(impactId, flight);
    if (landed) {
      this.spawnMeteorImpact(flight.target.x, flight.target.y, radius, flight.direction);
    }
  }

  cancelMeteorStrike(impactId: string): void {
    const flight = this.meteorFlights.get(impactId);
    if (flight) this.destroyMeteorFlight(impactId, flight);
  }

  private updateMeteorFlight(flight: {
    elapsed: number;
    target: { x: number; y: number };
    glow: Phaser.GameObjects.Arc;
    shell: Phaser.GameObjects.Ellipse;
    core: Phaser.GameObjects.Ellipse;
    trail: Phaser.GameObjects.Graphics;
    trailPoints: Array<{ x: number; y: number }>;
    lastEmberAt: number;
    direction: number;
  }): void {
    const mapWidth = this.scene.scale.width || 800;
    const deltaX = Math.max(280, mapWidth - flight.target.x + 36);
    const deltaY = Phaser.Math.Clamp(deltaX * 0.58, 200, 360);
    const startX = flight.target.x + deltaX;
    const startY = flight.target.y - deltaY;
    const eased = Phaser.Math.Clamp(flight.elapsed / 0.72, 0, 1) ** 3;
    const x = startX + (flight.target.x - startX) * eased;
    const y = startY + (flight.target.y - startY) * eased;
    flight.glow.setPosition(x, y);
    flight.shell.setPosition(x, y);
    flight.core.setPosition(x, y);

    const previous = flight.trailPoints[flight.trailPoints.length - 1];
    if (!previous || Math.hypot(x - previous.x, y - previous.y) >= 4) {
      flight.trailPoints.push({ x, y });
      if (flight.trailPoints.length > 26) flight.trailPoints.shift();
    }
    flight.trail.clear();
    for (let i = 1; i < flight.trailPoints.length; i++) {
      const from = flight.trailPoints[i - 1];
      const to = flight.trailPoints[i];
      const age = i / (flight.trailPoints.length - 1);
      flight.trail.lineStyle(18 - age * 13, 0xb93810, 0.08 + age * 0.2);
      flight.trail.beginPath();
      flight.trail.moveTo(from.x, from.y);
      flight.trail.lineTo(to.x, to.y);
      flight.trail.strokePath();
      flight.trail.lineStyle(9 - age * 6, 0xff4b12, 0.16 + age * 0.35);
      flight.trail.beginPath();
      flight.trail.moveTo(from.x, from.y);
      flight.trail.lineTo(to.x, to.y);
      flight.trail.strokePath();
      flight.trail.lineStyle(1 + age * 3, 0xffa21a, 0.12 + age * 0.32);
      flight.trail.beginPath();
      flight.trail.moveTo(from.x, from.y);
      flight.trail.lineTo(to.x, to.y);
      flight.trail.strokePath();
    }

    if (flight.elapsed - flight.lastEmberAt >= 0.042) {
      flight.lastEmberAt = flight.elapsed;
      const ember = this.scene.add.circle(
        x + Math.random() * 8,
        y - Math.random() * 8,
        1.5 + Math.random() * 2.5,
        Math.random() < 0.5 ? 0xff641c : 0xf59e0b,
        0.9,
      );
      ember.setBlendMode(Phaser.BlendModes.ADD).setDepth(12);
      this.particles.push({ obj: ember, active: true });
      this.scene.tweens.add({
        targets: ember,
        x: ember.x + 20 + Math.random() * 26,
        y: ember.y - 16 - Math.random() * 24,
        alpha: 0,
        scaleX: 0.15,
        scaleY: 0.15,
        duration: 220 + Math.random() * 120,
        ease: "Quad.easeOut",
        onComplete: () => {
          this.destroyObj(ember);
          const index = this.particles.findIndex((entry) => entry.obj === ember);
          if (index >= 0) this.particles.splice(index, 1);
        },
      });
    }
  }

  private destroyMeteorFlight(
    impactId: string,
    flight: {
      glow: Phaser.GameObjects.Arc;
      shell: Phaser.GameObjects.Ellipse;
      core: Phaser.GameObjects.Ellipse;
      trail: Phaser.GameObjects.Graphics;
    },
  ): void {
    this.meteorFlights.delete(impactId);
    for (const object of [flight.glow, flight.shell, flight.core]) {
      this.destroyObj(object);
      const index = this.particles.findIndex((entry) => entry.obj === object);
      if (index >= 0) this.particles.splice(index, 1);
    }
    this.destroyObj(flight.trail);
    const index = this.flashes.findIndex((entry) => entry.obj === flight.trail);
    if (index >= 0) this.flashes.splice(index, 1);
  }

  /** 在陨石抵达时生成暖色地面冲击环与沿飞行方向散射的熔岩碎屑。 */
  /** 在陨石抵达时生成暖色地面冲击环与沿飞行方向散射的熔岩碎屑。 */
  private spawnMeteorImpact(x: number, y: number, radius: number, direction: number): void {
    const scene = this.scene;
    const shockwave = scene.add.circle(x, y, radius, 0xf97316, 0.12);
    shockwave.setStrokeStyle(4, 0xff6b1a, 0.9).setScale(0.08).setDepth(12);
    this.flashes.push({ obj: shockwave, active: true });
    scene.tweens.add({
      targets: shockwave,
      scaleX: 1,
      scaleY: 1,
      alpha: 0,
      duration: 460,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.destroyObj(shockwave);
        const index = this.flashes.findIndex((entry) => entry.obj === shockwave);
        if (index >= 0) this.flashes.splice(index, 1);
      },
    });

    const groundPulse = scene.add.circle(x, y, radius * 0.22, 0xc2410c, 0.26);
    groundPulse.setBlendMode(Phaser.BlendModes.ADD).setDepth(11);
    this.flashes.push({ obj: groundPulse, active: true });
    scene.tweens.add({
      targets: groundPulse,
      scaleX: 2.1,
      scaleY: 2.1,
      alpha: 0,
      duration: 260,
      ease: "Quad.easeOut",
      onComplete: () => {
        this.destroyObj(groundPulse);
        const index = this.flashes.findIndex((entry) => entry.obj === groundPulse);
        if (index >= 0) this.flashes.splice(index, 1);
      },
    });

    const debrisCount = 18;
    for (let i = 0; i < debrisCount; i++) {
      const angle = direction + (Math.random() - 0.5) * Math.PI * 1.5;
      const distance = 36 + Math.random() * 92;
      const debris = scene.add.circle(
        x,
        y,
        2 + Math.random() * 3.5,
        [0xff4b12, 0xff7417, 0xf59e0b, 0x9a3412][Math.floor(Math.random() * 4)],
        0.95,
      );
      debris.setBlendMode(Phaser.BlendModes.ADD).setDepth(13);
      this.particles.push({ obj: debris, active: true });
      scene.tweens.add({
        targets: debris,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scaleX: 0.12,
        scaleY: 0.12,
        duration: 360 + Math.random() * 180,
        ease: "Cubic.easeOut",
        onComplete: () => {
          this.destroyObj(debris);
          const index = this.particles.findIndex((entry) => entry.obj === debris);
          if (index >= 0) this.particles.splice(index, 1);
        },
      });
    }
  }

  /** 生成范围攻击命中时的物理爆发环。 */
  spawnAreaImpact(x: number, y: number, radius: number): void {
    if (this.disabled || !Number.isFinite(radius) || radius <= 0) return;
    const color = 0xffb347;
    const flash = this.makeCircle(x, y, radius * 0.4, color, 0.24);
    this.flashes.push({ obj: flash, active: true });
    this.scene.tweens.add({
      targets: flash,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 260,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.destroyObj(flash);
        const index = this.flashes.findIndex((entry) => entry.obj === flash);
        if (index >= 0) this.flashes.splice(index, 1);
      },
    });
    const ring = this.makeCircle(x, y, radius, color, 0.3);
    (ring as Phaser.GameObjects.Arc).setStrokeStyle(5, 0xffe0a3, 0.9);
    (ring as Phaser.GameObjects.Arc).setScale(0.25);
    this.flashes.push({ obj: ring, active: true });
    this.scene.tweens.add({
      targets: ring,
      scaleX: 1,
      scaleY: 1,
      alpha: 0,
      duration: 420,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.destroyObj(ring);
        const index = this.flashes.findIndex((entry) => entry.obj === ring);
        if (index >= 0) this.flashes.splice(index, 1);
      },
    });
  }

  /** 绘制从雷塔到普通攻击主目标的即时闪电。 */
  spawnLightningStrike(origin: { x: number; y: number }, target: { x: number; y: number }): void {
    this.spawnLightningBolt(origin, target, 170);
  }

  /** 从主目标开始，按实际连锁顺序逐段绘制闪电。 */
  spawnChainLightning(
    origin: { x: number; y: number },
    targets: Array<{ x: number; y: number }>,
  ): void {
    if (this.disabled || targets.length === 0) return;
    let from = origin;
    for (const target of targets) {
      this.spawnLightningBolt(from, target, 260);
      from = target;
    }
  }

  private spawnLightningBolt(
    from: { x: number; y: number },
    to: { x: number; y: number },
    duration: number,
  ): void {
    if (this.disabled) return;
    const line = this.scene.add.graphics();
    const steps = 7;
    const path: Array<{ x: number; y: number }> = [from];
    for (let step = 1; step < steps; step++) {
      const progress = step / steps;
      const offset = (step % 2 === 0 ? -1 : 1) * 10;
      path.push({
        x: from.x + (to.x - from.x) * progress + offset,
        y: from.y + (to.y - from.y) * progress - offset,
      });
    }
    path.push(to);
    const drawBolt = (width: number, color: number, alpha: number) => {
      line.lineStyle(width, color, alpha);
      line.beginPath();
      line.moveTo(path[0].x, path[0].y);
      for (let pointIndex = 1; pointIndex < path.length; pointIndex++) {
        line.lineTo(path[pointIndex].x, path[pointIndex].y);
      }
      line.strokePath();
    };
    drawBolt(12, 0x147cff, 0.4);
    drawBolt(6, 0x38cfff, 0.95);
    drawBolt(2, 0xe6ffff, 1);
    line.setBlendMode(Phaser.BlendModes.ADD);
    line.setDepth(20);
    this.flashes.push({ obj: line, active: true });
    this.scene.tweens.add({
      targets: line,
      alpha: 0,
      duration,
      ease: "Quad.easeOut",
      onComplete: () => {
        this.destroyObj(line);
        const index = this.flashes.findIndex((entry) => entry.obj === line);
        if (index >= 0) this.flashes.splice(index, 1);
      },
    });
  }

  /** 生成冰锥命中时从中心扩散到最大范围的减速圈，附带冰晶碎片爆发效果。 */
  spawnIceConeImpact(x: number, y: number, radius: number): void {
    if (this.disabled || !Number.isFinite(radius) || radius <= 0) return;

    // 外圈：扩散到最大范围的冰蓝色减速圈
    const ring = this.makeCircle(x, y, radius, 0x7dd3fc, 0.08);
    (ring as Phaser.GameObjects.Arc).setStrokeStyle(3, 0xcffafe, 0.65);
    (ring as Phaser.GameObjects.Arc).setScale(0);
    this.flashes.push({ obj: ring, active: true });
    this.scene.tweens.add({
      targets: ring,
      scaleX: 1,
      scaleY: 1,
      alpha: 0,
      duration: 450,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.destroyObj(ring);
        const idx = this.flashes.findIndex((entry) => entry.obj === ring);
        if (idx >= 0) this.flashes.splice(idx, 1);
      },
    });

    // 冰晶碎片：向外散射的小型粒子，模拟碎裂效果
    const shatterColors = [0x7dd3fc, 0xcffafe, 0x38bdf8, 0xf0f9ff];
    const shatterCount = 12;
    for (let i = 0; i < shatterCount; i++) {
      const angle = (Math.PI * 2 * i) / shatterCount;
      const dist = radius * 0.6;
      const size = 2 + Math.random() * 3;
      const color = shatterColors[Math.floor(Math.random() * shatterColors.length)];
      const shard = this.makeCircle(x, y, size, color, 1);
      this.particles.push({ obj: shard, active: true });
      this.scene.tweens.add({
        targets: shard,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: 380,
        ease: "Quart.easeOut",
        onComplete: () => {
          this.destroyObj(shard);
          const idx = this.particles.findIndex((entry) => entry.obj === shard);
          if (idx >= 0) this.particles.splice(idx, 1);
        },
      });
    }
  }

  /** 生成一个向上浮动并渐隐的伤害数字。 */
  spawnDamageNumber(
    x: number,
    y: number,
    damage: number,
    damageType: DamageType,
    isCrit: boolean = false,
  ): void {
    if (this.disabled) return;
    const cfg = this.dmgConfig;
    const colorHex = "#" + (cfg.colors[damageType] ?? 0xffffff).toString(16).padStart(6, "0");
    const text = this.scene.add.text(x, y, Math.round(damage).toString(), {
      fontSize: cfg.fontSize,
      fontFamily: cfg.fontFamily,
      color: colorHex,
      fontStyle: isCrit ? "bold" : "normal",
      stroke: "#000000",
      strokeThickness: isCrit ? 3 : 2,
    });
    text.setOrigin(0.5);
    this.texts.push({ obj: text, active: true });
    const flyUp = isCrit ? -18 : -12;
    this.scene.tweens.add({
      targets: text,
      y: y + flyUp - (cfg.floatSpeed * cfg.lifetime) / 2,
      alpha: 0,
      duration: cfg.lifetime * 1000,
      ease: "Power1.out",
      onComplete: () => {
        text.destroy();
        const idx = this.texts.findIndex((t) => t.obj === text);
        if (idx >= 0) this.texts.splice(idx, 1);
      },
    });
  }

  /** 在塔位置生成短暂的金色脉冲。 */
  spawnUpgradeFlash(x: number, y: number, radius: number = this.upgradeConfig.radius): void {
    if (this.disabled) return;
    const cfg = this.upgradeConfig;
    const flash = this.makeCircle(x, y, radius, 0xfbbf24, cfg.initialAlpha);
    this.flashes.push({ obj: flash, active: true });
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scaleX: 1.6,
      scaleY: 1.6,
      duration: cfg.lifetime * 1000,
      ease: "Power2.out",
      onComplete: () => {
        this.destroyObj(flash);
        const idx = this.flashes.findIndex((p) => p.obj === flash);
        if (idx >= 0) this.flashes.splice(idx, 1);
      },
    });
  }

  // ─── 每帧维护 ──────────────────────────────────────────────────

  /**
   * Call once per render frame. Cleans up any stale references whose
   * Phaser tweens have already destroyed the underlying objects.
   */
  update(): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      if (!this.particles[i].active) this.particles.splice(i, 1);
    }
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      if (!this.flashes[i].active) this.flashes.splice(i, 1);
    }
    for (let i = this.texts.length - 1; i >= 0; i--) {
      if (!this.texts[i].active) this.texts.splice(i, 1);
    }
  }

  /** 清理所有剩余的特效对象。 */
  destroy(): void {
    for (const impactId of this.meteorFlights.keys()) {
      this.cancelMeteorStrike(impactId);
    }
    this.clearGlacialStorms();
    for (const entry of this.particles) {
      this.destroyObj(entry.obj);
    }
    this.particles.length = 0;
    for (const entry of this.flashes) {
      this.destroyObj(entry.obj);
    }
    this.flashes.length = 0;
    for (const entry of this.texts) {
      entry.obj.destroy();
    }
    this.texts.length = 0;
  }

  private destroyObj(obj: unknown): void {
    if (obj && typeof (obj as Record<string, unknown>)["destroy"] === "function") {
      (obj as { destroy(): void }).destroy();
    }
  }

  private spawnFlash(x: number, y: number, color: number, alpha: number, lifetime: number): void {
    const flash = this.makeCircle(x, y, 50, color, alpha);
    this.flashes.push({ obj: flash, active: true });
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: lifetime * 1000,
      ease: "Power2.out",
      onComplete: () => {
        this.destroyObj(flash);
        const idx = this.flashes.findIndex((p) => p.obj === flash);
        if (idx >= 0) this.flashes.splice(idx, 1);
      },
    });
  }
}
